/**
 * Default configuration for SocialShare
 */

import type { SocialShareConfig, PlatformId, BlockShareConfig } from '../types';

/** Default platform labels */
export const DEFAULT_LABELS: Record<PlatformId, string> = {
  twitter: 'Share on X',
  facebook: 'Share on Facebook',
  pinterest: 'Pin it',
  reddit: 'Share on Reddit',
  linkedin: 'Share on LinkedIn',
  vk: 'Share on VK',
  ok: 'Share on OK',
  weibo: 'Share on Weibo',
  qzone: 'Share on QZone',
  line: 'Share on LINE',
  kakao: 'Share on KakaoStory',
  naver: 'Share on Naver',
  messenger: 'Send via Messenger',
  tumblr: 'Share on Tumblr',
  plurk: 'Share on Plurk',
  whatsapp: 'Share on WhatsApp',
  telegram: 'Share on Telegram',
  email: 'Share via Email',
  hatena: 'はてなブックマーク',
  threads: 'Share on Threads',
  x: 'Share on X',
  copylink: 'Copy link',
  snapchat: 'Share on Snapchat',
  instagram: 'Share on Instagram',
  discord: 'Share on Discord',
  viber: 'Share on Viber',
  classroom: 'Share to Classroom',
};

/** Default block mode configuration */
export const DEFAULT_BLOCK_CONFIG: Required<BlockShareConfig> = {
  radius: 4,
  gap: 1,
  height: 40,
  items: {},
};

/** Default configuration */
export const DEFAULT_CONFIG: Required<Omit<SocialShareConfig, 'platforms' | 'onShare' | 'onCopy' | 'cssPath' | 'target' | 'blockConfig'>> & {
  platforms: PlatformId[];
  target: string | undefined;
  blockConfig: BlockShareConfig;
} = {
  platforms: ['twitter', 'facebook', 'pinterest', 'copylink'],
  content: {
    url: typeof window !== 'undefined' ? window.location.href : '',
    title: typeof document !== 'undefined' ? document.title : '',
    description: '',
    image: '',
    hashtags: [],
  },
  position: 'auto',
  mode: 'icon', // Default to icon only
  shape: 'circle', // Default to circle
  target: undefined,
  autoWidthThreshold: 780,
  iconSpritePath: 'https://k.areal.me/dist/s6.svg',
  iconSize: 45,
  buttonOpacity: 0.84,
  buttonMargin: 8,
  buttonPadding: 0,
  labels: DEFAULT_LABELS,
  zIndex: 9999,
  blockConfig: {},
};

/** Merge user config with defaults */
export function mergeConfig(userConfig: SocialShareConfig): Required<Omit<SocialShareConfig, 'onShare' | 'onCopy' | 'cssPath' | 'target' | 'blockConfig'>> & Pick<SocialShareConfig, 'onShare' | 'onCopy' | 'cssPath' | 'target'> & { blockConfig: BlockShareConfig } {
  // Normalize legacy mode values
  let mode = userConfig.mode;
  if (mode === 'circle') mode = 'icon';
  if (mode === 'text') mode = 'block';
  
  // If user passed legacy 'mode: block', we might want to imply shape='square' if not specified
  // But let's stick to defaults for predictability unless explicitly set
  
  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    mode: mode ?? DEFAULT_CONFIG.mode,
    shape: userConfig.shape ?? DEFAULT_CONFIG.shape,
    content: {
      ...DEFAULT_CONFIG.content,
      ...userConfig.content,
    },
    labels: {
      ...DEFAULT_LABELS,
      ...userConfig.labels,
    },
    blockConfig: {
      ...DEFAULT_BLOCK_CONFIG, // Use default block config as base
      ...userConfig.blockConfig,
      items: {
        ...DEFAULT_BLOCK_CONFIG.items,
        ...userConfig.blockConfig?.items,
      },
    },
  };
}
